import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/button";
import { Header } from "components/header";
import { Footer } from "components/footer/footer";
import stores from "consts/stores.json";
import { getQueryParams, useCurrentLocation } from "lib/utils";
import AuthContext from "contexts/AuthContext";
import ScrollToTop from "components/scroll-to-top";
import { Link, useLocation } from "react-router-dom";
import useIsMobile from "hooks/useIsMobile";
import { TradeInIcon } from "pages/svgs/tradein-logo";
import { COUNTRY_NAME, LOCALE_NAME, STATE_NAME } from "consts/query-params";
// import LegoWoman from "assets/lego-woman.png";
import HowToPrepare1 from "assets/howtoprepare-1.jpg";
import HowToPrepare2Mail from "assets/howtoprepare-2-mail.jpg";
import HowToPrepare3Mail from "assets/howtoprepare-3-mail.jpg";
import HowToPrepare2Store from "assets/howtoprepare-2-store.jpg";
import HowToPrepare3Store from "assets/howtoprepare-3-store.jpg";
import WhatHappens1 from "assets/whathappens-1.jpg";
import WhatHappens1Store from "assets/whathappens-1-store.jpg";
import WhatHappens2 from "assets/whathappens-2.jpg";
import WhatHappens3 from "assets/whathappens-3.jpg";
import { Helmet } from "react-helmet";

const HowItWorks = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { location: currentLocation } = useCurrentLocation();
  const queryParams = getQueryParams(location.search);
  const [takeBackTypeToggle, setTakeBackTypeToggle] = useState(
    queryParams?.preselected === "in-store" ? "store" : "mail"
  );

  const [showInStore, setShowInStore] = useState(false);

  const store = currentLocation?.takebackMethod?.find((method) =>
    method.startsWith("in_store")
  )
    ? stores.find(
        (store) =>
          store.value ===
          currentLocation.takebackMethod.find((method) =>
            method.startsWith("in_store")
          )
      )
    : null;
  const storeLocation = store?.name;
  const storeAddress = store?.address;

  const updateShowInStore = useCallback(() => {
    setShowInStore(
      currentLocation.takebackMethod.some((method) =>
        stores.some((store) => store.value === method)
      )
    );
  }, [currentLocation]);

  useEffect(() => {
    updateShowInStore();
  }, [updateShowInStore]);

  const isKiosk = currentLocation?.takebackMethod
    ? stores.some(
        (store) =>
          currentLocation.takebackMethod.includes(store.value) &&
          store.return_type === "kiosk"
      )
    : false;

  const { user } = useContext(AuthContext);

  // const moveTo = (elementId, offset = 0) => {
  //   const element = document.getElementById(elementId);
  //   if (element) {
  //     const elementPosition =
  //       element.getBoundingClientRect().top + window.scrollY;
  //     const offsetPosition = elementPosition - offset;
  //     window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  //   }
  // };

  // const handleScrollTo = (e, elementId, offset) => {
  //   e.preventDefault();
  //   moveTo(elementId, offset);
  // };

  const isMobile = useIsMobile();
  const navigationLink = `${t("urls.my_trade_ins")}/${location.search}`;
  const mobileContent = (
    <>
      <Link to={navigationLink}>
        <TradeInIcon />
      </Link>
    </>
  );

  const CtaButton = ({ className }) => {
    return (
      <Button
        tracking="Start trade-in clicked"
        href={`${t("urls.trade_in_weight")}`}
        queryParams={{
          [LOCALE_NAME]: currentLocation.locale,
          [STATE_NAME]: currentLocation.state,
          [COUNTRY_NAME]: currentLocation.country,
        }}
        className={className}
      >
        {t("landing.button_start_new")}
      </Button>
    );
  };

  return (
    <div>
      <ScrollToTop />
      <Helmet>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "How It Works",
            "description": "Trading in pre-loved LEGO® bricks is rewarding. Here's how you can earn a LEGO e-Gift Card.",
            "url": "https://legobricktakeback.com/en-US/how-it-works"
          }
        `}</script>
      </Helmet>
      <Header mobileChildren={mobileContent} classNames="bg-yellow border-none">
        <div className="flex gap-large">
          <CtaButton className="hidden lg:block" />

          {!isMobile && (
            <Button
              href={user ? `${t("urls.my_trade_ins")}` : `${t("urls.login")}`}
              variant="secondary_no_border"
              queryParams={queryParams}
            >
              {user
                ? t("landing.button_my_trade_ins")
                : t("landing.button_sign_in")}
            </Button>
          )}
        </div>
      </Header>
      <h1 className="py-12 bg-blue-300/25 flex flex-col items-center">
        <h1 className="text-primary text-[30px] md:text-[40px] font-bold text-center py-medium">
          How It Works
        </h1>
        <p className="text-xl max-w-lg text-center text-secondary">
          Trading in pre-loved LEGO® bricks is rewarding. Here's how you can
          earn a LEGO e-Gift Card.
        </p>
      </h1>
      <div className="py-xx-large flex flex-col gap-xxxx-large">
        <div id="howItWorks">
          <div className="flex flex-col justify-center px-x-large md:px-xx-large ">
            <div className="mt-xx-large md:mt-xxx-large">
              {showInStore ? (
                <div className="mb-x-large md:mb-xx-large pb-xxx-large">
                  <h3 className="text-primary text-2xl font-bold md:text-4xl text-center">
                    Choose your trade-in method to see how it works:
                  </h3>
                  <div className="flex flex-col md:flex-row lg:max-w-4xl mx-auto mt-regular md:mt-large bg-gray-300/25 border border-gray-300 rounded-xl p-x-small text-primary">
                    <button
                      onClick={() => setTakeBackTypeToggle("mail")}
                      className={`${
                        takeBackTypeToggle === "mail"
                          ? "bg-blue text-white "
                          : ""
                      }rounded-lg py-regular px-x-large text-2xl flex flex-1 items-center justify-center`}
                    >
                      <span
                        className={`material-symbols-outlined align-bottom mr-large scale-125 ${
                          takeBackTypeToggle === "mail"
                            ? "text-white"
                            : "text-[#757575]"
                        }`}
                      >
                        package_2
                      </span>
                      <span className="block truncate leading-10">
                        {t("landing.method_mail")}
                      </span>
                    </button>
                    <button
                      onClick={() => setTakeBackTypeToggle("store")}
                      className={`${
                        takeBackTypeToggle === "store"
                          ? "bg-blue text-white "
                          : ""
                      }rounded-lg py-regular px-x-large text-2xl flex flex-1 items-center justify-center`}
                    >
                      <span
                        className={`material-symbols-outlined align-bottom mr-large scale-125 ${
                          takeBackTypeToggle === "store"
                            ? "text-white"
                            : "text-[#757575]"
                        }`}
                      >
                        storefront
                      </span>
                      <span className="block truncate leading-10">
                        {t("landing.method_store", {
                          store: storeLocation,
                          asterisk: takeBackTypeToggle === "store" ? "*" : "",
                        })}
                      </span>
                    </button>
                  </div>

                  {takeBackTypeToggle === "store" && (
                    <div className="text-black-300 w-full text-center mt-large text-sm">
                      <span className="font-semibold ">*Address:</span>{" "}
                      {storeAddress}
                    </div>
                  )}
                </div>
              ) : (
                ""
              )}
              <h3 className="text-primary text-2xl font-bold md:text-4xl text-center pb-xxxx-large">
                How to prepare for{" "}
                {takeBackTypeToggle === "store"
                  ? isKiosk
                    ? "an In-Store trade-in"
                    : "an In-Store Drop-Off trade-in"
                  : "a Mail-In trade-in"}
              </h3>
              <div className="flex flex-col lg:flex-row justify-stretch mx-auto space-y-xxx-large lg:space-y-0 lg:gap-xxx-large lg:max-w-6xl">
                <div className="md:basis-1/3 flex flex-col md:flex-row-reverse lg:flex-col lg:max-w-[357px]">
                  <div className="md:basis-1/2">
                    <img
                      className="w-full h-full object-cover"
                      src={HowToPrepare1}
                      alt="Gather your LEGO bricks"
                    />
                  </div>

                  <div className="p-x-large bg-[#F27173] flex flex-col md:basis-1/2 grow justify-between min-h-[230px]">
                    <div className="mb-large">
                      <div className="flex space-x-large mb-large">
                        <span className="bg-white text-center text-primary leading-[24px] font-bold text-2xl rounded-full w-6 h-6 shrink-0">
                          1
                        </span>
                        <h4 className="text-primary text-3xl font-bold">
                          Prepare your LEGO® bricks
                        </h4>
                      </div>
                      {takeBackTypeToggle === "mail" ? (
                        <p className="text-secondary text-base">
                          First, disassemble your bricks. Then, follow the
                          instructions in the{" "}
                          <Link
                            to={{
                              pathname: t("urls.preparation_guidelines"),
                              search: `?${new URLSearchParams(queryParams).toString()}`,
                            }}
                            className="text-white"
                          >
                            Preparation Guidelines
                          </Link>
                          .
                        </p>
                      ) : (
                        <p className="text-secondary text-base">
                          First, disassemble your bricks. Then, follow the
                          step-by-step instructions in the Preparation
                          Guidelines.
                        </p>
                      )}
                    </div>
                    {/* <div className="">
                      <Button
                        onClick={(e) =>
                          handleScrollTo(
                            e,
                            takeBackTypeToggle === "store"
                              ? "preparation"
                              : "what-we-accept",
                            80
                          )
                        }
                        variant="secondary"
                        icon="arrow_downward"
                        className="w-fit"
                      >
                        {takeBackTypeToggle === "store"
                          ? "Preparation checklist"
                          : "See What We Accept"}
                      </Button>
                    </div> */}
                  </div>
                </div>

                {takeBackTypeToggle === "mail" ? (
                  <div className="md:basis-1/3 flex flex-col md:flex-row-reverse lg:flex-col lg:max-w-[357px]">
                    <div className="md:basis-1/2">
                      <img
                        src={HowToPrepare2Mail}
                        alt="Pack up your bricks"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-x-large bg-[#E980B3] flex flex-col grow md:basis-1/2 justify-between min-h-[230px]">
                      <div className="mb-large">
                        <div className="flex space-x-large mb-large">
                          <span className="bg-white text-center text-primary leading-[24px] font-bold text-2xl rounded-full w-6 h-6 shrink-0">
                            2
                          </span>
                          <h4 className="text-primary text-3xl font-bold">
                            Pack up your bricks
                          </h4>
                        </div>
                        <p className="text-secondary text-base">
                          Once you’ve sorted your bricks, pack them in a sturdy
                          cardboard box for shipping.
                        </p>
                      </div>
                      {/* <div>
                        <Button
                          onClick={(e) =>
                            handleScrollTo(e, "packaging-guidelines", 80)
                          }
                          icon="arrow_downward"
                          variant="secondary"
                          className="w-fit"
                        >
                          Packaging guidelines
                        </Button>
                      </div> */}
                    </div>
                  </div>
                ) : (
                  <div className="md:basis-1/3 flex flex-col md:flex-row-reverse lg:flex-col lg:max-w-[357px]">
                    <div className="md:basis-1/2">
                      <img
                        src={HowToPrepare2Store}
                        alt="Send your bricks"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-x-large bg-[#E980B3] flex flex-col md:basis-1/2 grow justify-between min-h-[230px]">
                      <div className="mb-large">
                        <div className="flex space-x-large mb-large">
                          <span className="bg-white text-center text-primary leading-[24px] font-bold text-2xl rounded-full w-6 h-6 shrink-0">
                            2
                          </span>
                          <h4 className="text-primary text-3xl font-bold">
                            {isKiosk
                              ? "Schedule your in-store appointment"
                              : "Create a trade-in"}
                          </h4>
                        </div>
                        <p className="text-secondary text-base">
                          {isKiosk
                            ? "Start your trade-in. Choose the in-store option when prompted and pick your appointment date and time. Pack up your bricks to bring to the LEGO Store."
                            : "Start your trade-in. Choose the in-store option when prompted. Pack up your bricks in a sturdy cardboard shipping box and leave it unsealed."}
                        </p>
                      </div>
                      {/* <div>
                        <Button
                          href="https://www.ups.com/dropoff/?loc=en_US"
                          externalLink
                          variant="secondary"
                          icon="open_in_new"
                        >
                          Find my nearest UPS drop-off
                        </Button>
                      </div> */}
                    </div>
                  </div>
                )}
                {takeBackTypeToggle === "mail" ? (
                  <div className="md:basis-1/3 flex flex-col md:flex-row-reverse lg:flex-col lg:max-w-[357px]">
                    <div className="md:basis-1/2">
                      <img
                        src={HowToPrepare3Mail}
                        alt="Create a trade-in"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-x-large bg-[#F79952] flex flex-col md:basis-1/2 grow justify-between min-h-[230px]">
                      <div className="mb-large">
                        <div className="flex space-x-large mb-large">
                          <span className="bg-white text-center text-primary leading-[24px] font-bold text-2xl rounded-full w-6 h-6 shrink-0">
                            3
                          </span>
                          <h4 className="text-primary text-3xl font-bold">
                            Send your bricks
                          </h4>
                        </div>
                        <p className="text-secondary text-base">
                          Once you’ve sealed your trade-in, print a free
                          shipping label or generate a shipping QR code and take
                          your package to the nearest{" "}
                          <Link
                            to="https://www.ups.com/dropoff/?loc=en_US"
                            externalLink
                            className="text-white"
                          >
                            UPS location
                          </Link>
                          .
                        </p>
                      </div>
                      {/* <div>
                        <Button
                          href={t("urls.trade_in_weight")}
                          variant="secondary"
                          icon="arrow_forward"
                        >
                          Start Trade-In
                        </Button>
                      </div> */}
                    </div>
                  </div>
                ) : (
                  <div className="md:basis-1/3 flex flex-col md:flex-row-reverse lg:flex-col lg:max-w-[357px]">
                    <div className="md:basis-1/2">
                      <img
                        src={HowToPrepare3Store}
                        alt="Visit store"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-x-large bg-[#F79952] flex flex-col md:basis-1/2 grow justify-between min-h-[230px]">
                      <div className="mb-large">
                        <div className="flex space-x-large mb-large">
                          <span className="bg-white text-center text-primary leading-[24px] font-bold text-2xl rounded-full w-6 h-6 shrink-0">
                            3
                          </span>
                          <h4 className="text-primary text-3xl font-bold">
                            {t("landing.how_it_works.step_2_in_store.heading", {
                              store: storeLocation,
                            })}
                          </h4>
                        </div>
                        <p className="text-secondary text-base">
                          {isKiosk
                            ? "Bring your bricks to the scheduled appointment time and have your check-in QR code ready to show to a Brick Specialist."
                            : "Bring your bricks to the store and have your check-in QR code ready to show to a Brick Specialist."}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-xx-large flex flex-col justify-center items-center space-y-x-large mt-xx-large">
          <h3 className="text-primary text-2xl font-bold md:text-4xl text-center">
            Want to know how to prepare your trade-in?
          </h3>
          <Button
            href={t("urls.preparation_guidelines")}
            queryParams={queryParams}
          >
            View Preparation Guidelines
          </Button>
        </div>

        <div id="whathappens">
          <div className="flex flex-col justify-center px-x-large md:px-xx-large ">
            <div className="mt-xx-large md:mt-xxx-large">
              <h3 className="text-primary text-2xl font-bold md:text-4xl text-center pb-xxxx-large">
                {takeBackTypeToggle === "store"
                  ? "What happens when I arrive in store?"
                  : "What happens after I send my bricks?"}
              </h3>
              <div className="flex flex-col lg:flex-row justify-stretch mx-auto space-y-xxx-large lg:space-y-0 lg:gap-xxx-large lg:max-w-6xl">
                <div className="md:basis-1/3 flex flex-col md:flex-row-reverse lg:flex-col lg:max-w-[357px]">
                  <div className="md:basis-1/2">
                    <img
                      className="w-full h-full object-cover"
                      src={
                        takeBackTypeToggle === "store"
                          ? WhatHappens1Store
                          : WhatHappens1
                      }
                      alt={
                        takeBackTypeToggle === "store"
                          ? "You’re checked in"
                          : "Your bricks make their way to us"
                      }
                    />
                  </div>

                  <div
                    className={`p-x-large bg-[#4FA566] flex flex-col md:basis-1/2 grow justify-between ${takeBackTypeToggle === "mail" ? "min-h-[250px]" : ""}`}
                  >
                    <div className="mb-large">
                      <div className="flex space-x-large mb-large">
                        <span className="bg-white text-center text-primary leading-[24px] font-bold text-2xl rounded-full w-6 h-6 shrink-0">
                          4
                        </span>
                        <h4 className="text-primary text-3xl font-bold">
                          {takeBackTypeToggle === "store"
                            ? "You’re checked in"
                            : "Your bricks make their way to us"}
                        </h4>
                      </div>

                      {takeBackTypeToggle === "store" ? (
                        <p className="text-secondary text-base">
                          When you arrive in store, show your check-in QR code —
                          you’ll find it in your confirmation email or the{" "}
                          <Link
                            to={{
                              pathname: t("urls.my_trade_ins"),
                              search: `?${new URLSearchParams(queryParams).toString()}`,
                            }}
                            className="text-white"
                          >
                            My Trade-Ins Dashboard
                          </Link>
                          . Our Brick Specialists will scan it to check you in.
                        </p>
                      ) : (
                        <p className="text-secondary text-base">
                          Once you hand off your bricks, their journey begins!
                          Sign in to track them all the way to us in your{" "}
                          <Link
                            to={{
                              pathname: t("urls.my_trade_ins"),
                              search: `?${new URLSearchParams(queryParams).toString()}`,
                            }}
                            className="text-white"
                          >
                            My Trade-Ins Dashboard
                          </Link>
                          .
                        </p>
                      )}
                    </div>
                    {/* {takeBackTypeToggle === "mail" && (
                      <div className="">
                        <Button
                          href={user ? t("urls.my_trade_ins") : t("urls.login")}
                          variant="secondary"
                          icon="arrow_forward"
                          className="w-fit"
                        >
                          {!user && "Sign In and "}View Your Trade-Ins
                        </Button>
                      </div>
                    )} */}
                  </div>
                </div>
                <div className="md:basis-1/3 flex flex-col md:flex-row-reverse lg:flex-col lg:max-w-[357px]">
                  <div className="md:basis-1/2">
                    <img
                      src={WhatHappens2}
                      alt="We verify your bricks"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div
                    className={`p-x-large bg-[#4F9ACF] flex flex-col grow md:basis-1/2 justify-between ${takeBackTypeToggle === "mail" ? "min-h-[250px]" : ""}`}
                  >
                    <div className="mb-large">
                      <div className="flex space-x-large mb-large">
                        <span className="bg-white text-center text-primary leading-[24px] font-bold text-2xl rounded-full w-6 h-6 shrink-0">
                          5
                        </span>
                        <h4 className="text-primary text-3xl font-bold">
                          {takeBackTypeToggle === "mail"
                            ? "We verify your bricks"
                            : "Your bricks are verified"}
                        </h4>
                      </div>
                      <p className="text-secondary text-base">
                        {takeBackTypeToggle === "mail"
                          ? "Once your trade-in arrives, our brick experts verify and weigh every brick to ensure that you get your well-earned reward."
                          : isKiosk
                            ? "After check-in, you’ll be asked to pour your LEGO bricks into the weighing tray at the kiosk. We’ll walk you through the process as we weigh and verify your trade-in."
                            : "Once you’re checked-in, one of our Brick Specialists will verify and weigh your bricks."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="md:basis-1/3 flex flex-col md:flex-row-reverse lg:flex-col lg:max-w-[357px]">
                  <div className="md:basis-1/2">
                    <img
                      src={WhatHappens3}
                      alt="You get all the credit"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div
                    className={`p-x-large bg-[#9E76B4] flex flex-col md:basis-1/2 grow justify-between ${takeBackTypeToggle === "mail" ? "min-h-[250px]" : ""}`}
                  >
                    <div className="mb-large">
                      <div className="flex space-x-large mb-large">
                        <span className="bg-white text-center text-primary leading-[24px] font-bold text-2xl rounded-full w-6 h-6 shrink-0">
                          6
                        </span>
                        <h4 className="text-primary text-3xl font-bold">
                          You get all the credit
                        </h4>
                      </div>
                      <p className="text-secondary text-base">
                        Once we verify your bricks, you'll get a LEGO e-Gift
                        Card by email, ready to spend however you like!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-xx-large flex flex-col justify-center items-center space-y-x-large mt-xx-large mb-xxx-large">
          <h3 className="text-primary text-2xl font-bold md:text-4xl text-center">
            Turn your bricks into rewards
          </h3>
          <CtaButton />
        </div>

        {/* <div className="border border-gray-300 rounded-xl flex relative max-w-2xl md:pr-[282px] mb-xxxx-large p-1 mx-6 md:mx-auto">
          <div className="p-x-large flex flex-col gap-regular w-full space-y-small">
            <h3 className="text-primary text-2xl font-bold md:text-4xl">
              Got questions?
            </h3>
            <p className="text-primary">
              Learn how to prepare your trade-in and more details on the LEGO
              Brick Take Back program.
            </p>
            <Link
              to={{
                pathname: t("urls.faq"),
                search: `?${new URLSearchParams(queryParams).toString()}`,
              }}
              className="select-none flex items-center justify-center whitespace-nowrap rounded-full font-medium ring-offset-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-gray-950 dark:focus-visible:ring-gray-300 bg-blue hover:bg-blue-600 text-white disabled:bg-blue-300 w-fit px-[24px] py-regular leading-7"
            >
              Visit FAQ
            </Link>
          </div>
          <img
            src={LegoWoman}
            alt="Smiling LEGO Minifigure"
            className="md:absolute md:right-large md:bottom-0 w-[282px] md:w-[246px] hidden sm:block"
          />
        </div> */}
      </div>
      <Footer />
    </div>
  );
};

export default HowItWorks;
